import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Lightbulb, TrendingUp, CheckCircle, Users, MessageSquare, Search } from 'lucide-react'; // Icons for sections

// Using a placeholder image for the hero section, replace with your own
// You might need to create a simple image like a blurred city or abstract pattern for a 'transparent' feel
import heroPlacementImg from '../../assets/PlacementImg/PlacementHero.jpg'; // Create or find a suitable background image
import { Link } from 'react-router-dom';

// You might also want to import your previous hero image if it's suitable,
// but for a 'transparent' look, a less busy image might be better.
// import heroImg from "../assets/faq-img.jpg";

const features = [
  {
    icon: <Briefcase className="h-8 w-8 text-blue-600" />,
    title: 'Diverse Opportunities',
    description: 'Access a wide range of placements across various industries and roles.',
  },
  {
    icon: <Lightbulb className="h-8 w-8 text-yellow-600" />,
    title: 'Skill Enhancement',
    description: 'Gain practical experience and develop in-demand skills relevant to your career.',
  },
  {
    icon: <TrendingUp className="h-8 w-8 text-green-600" />,
    title: 'Career Growth',
    description: 'Kickstart your career with leading companies and accelerate your professional journey.',
  },
  {
    icon: <Users className="h-8 w-8 text-purple-600" />,
    title: 'Mentorship & Support',
    description: 'Receive guidance from industry experts and a dedicated support team.',
  },
];

const howItWorksSteps = [
  {
    step: 1,
    title: 'Sign Up & Create Profile',
    description: 'Register on our platform and build a comprehensive profile highlighting your skills and aspirations.',
    icon: <CheckCircle className="h-10 w-10 text-emerald-500" />,
  },
  {
    step: 2,
    title: 'Explore & Discover',
    description: 'Browse through a curated list of placement opportunities tailored to your interests.',
    icon: <Search className="h-10 w-10 text-orange-500" />, // Reusing Search icon from lucide-react
  },
  {
    step: 3,
    title: 'Apply & Interview',
    description: 'Submit applications for your preferred roles and prepare for interviews with companies.',
    icon: <MessageSquare className="h-10 w-10 text-sky-500" />,
  },
  {
    step: 4,
    title: 'Get Placed!',
    description: 'Secure your dream placement and embark on a rewarding professional journey.',
    icon: <TrendingUp className="h-10 w-10 text-indigo-500" />,
  },
];

const PlacementPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800">
      {/* Hero Section */}
      <section
        className="relative h-[500px] flex items-center justify-center text-center p-6 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroPlacementImg})` }}
      >
        {/* Overlay for a "transparent" feel and better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <motion.div
          className="relative z-10 text-white max-w-4xl mx-auto"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight pt-72">
           <span className='text-orange-600'> Unlock Your Career Potential</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 font-light">
            Connecting talent with unparalleled opportunities for growth and success.
          </p>
          <motion.button
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full text-lg shadow-lg transform transition-transform duration-300 hover:scale-105"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <a
  href="https://nadezhda.in/nvexam/"
  target="_blank"
  rel="noopener noreferrer"
  className="text-white "
>
  Apply for Placements Now
</a>

          </motion.button>
        </motion.div>
      </section>

      {/* Features/Benefits Section */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">Why Choose Our Placement Program?</h2>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center"
              variants={itemVariants}
            >
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* How It Works Section */}
      <section className="bg-gradient-to-r from-blue-500 to-purple-600 py-16 px-6">
        <h2 className="text-4xl font-bold text-center text-white mb-12">How Our Placement Process Works</h2>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {howItWorksSteps.map((step, index) => (
            <motion.div
              key={index}
              className="bg-white p-8 rounded-xl shadow-lg flex flex-col items-center text-center relative overflow-hidden"
              variants={itemVariants}
            >
              <div className="absolute -top-4 -right-4 bg-orange-500 text-white text-6xl font-extrabold p-4 rounded-full ">
                {step.step}
              </div>
              <div className="mb-4 z-10">{step.icon}</div>
              <h3 className="text-xl font-semibold mb-3 z-10">{step.title}</h3>
              <p className="text-gray-600 z-10">{step.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Call to Action Section (Optional: add a testimonial section here) */}
      <section className="bg-gray-800 text-white py-16 px-6 text-center">
        <h2 className="text-4xl font-bold mb-6">Ready to Find Your Dream Placement?</h2>
        <p className="text-xl mb-8 max-w-3xl mx-auto">
          Join hundreds of successful candidates who found their ideal career paths through our platform.
        </p>
        <motion.button
          className="bg-blue-700 hover:bg-blue-500 text-white font-bold py-4 px-10 rounded-full text-xl shadow-lg transform transition-transform duration-300 hover:scale-105"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
           <a
  href="https://nadezhda.in/nvexam/"
  target="_blank"
  rel="noopener noreferrer"
  className="text-white "
>
  Get Started Today!
</a>
        
        </motion.button>
      </section>

       <div className="mt-8 text-center">
                    <img
                        src="https://www.pynetlabs.com/wp-content/uploads/2024/11/All-pointer.webp"
                        alt="Placement Success"
                        className="mx-auto mb-4 rounded-full shadow-lg w-full h-full max-w-20xs"
                    />
                    <p className="text-gray-600">
                        We are proud of our students' achievements and the opportunities they have secured.
                    </p>
                <div className="mt-8 text-center">
                    <p className="text-gray-600">
                        Congratulations to all our placed students!
                    </p>
                </div>
            </div>

    </div>
  );
};

export default PlacementPage;